import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Checkbox,
  IconButton,
  Paper,
  styled,
  Grid,
  Tooltip,
  Switch,
  Stack,
} from "@mui/material";
import { ExpandMore, ExpandLess, Undo, Redo } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../redux/store";
import {
  popRedoStacks,
  popUndoStacks,
  pushRedoStacks,
  pushUndoStacks,
  setCleanUpDone,
  setRooms,
  setWalls,
  setDrawLine,
  clearDrawLine,
  setDrawWindow,
  clearDrawWindow,
  setDrawDoor,
  clearDrawDoor,
  setAssetType,
  setDoorAssets,
  setPlot,
  togglePlot,
  setSelectedWall,
  setSelectedWallOption,
  setActiveTool,
  clearRedoStacks,
  setWindowAssets,
  toggleLockRooms,
  setLockRooms,
  setWallTransformationUsed,
} from "../../../redux/features/appState";
import { showToast } from "../../../redux/features/toast/toast.slice";
import { getFloorplanLimits } from "../utils/helpers";
import { Wall } from "./Floorplan";
import { v4 as uuidv4 } from "uuid";
import SelectTool from "../../../assets/select-tool.svg";
import { toggleUnit } from "../../../redux/features/appState";

const ToolbarContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  right: "2.5%",
  top: 100,
  bottom: 0,
  width: 280,
  border: "2px solid black",
  height: "87.5%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 7,
  boxShadow: theme.shadows[3],
  overflow: "hidden",
  zIndex: 1200,
}));

const ToolbarHeader = styled(Box)(({ theme }) => ({
  backgroundColor: "#FF9A56",
  color: "black",
  padding: theme.spacing(1.5),
  textAlign: "center",
  borderTopLeftRadius: 7,
  borderTopRightRadius: 7,
}));

const ToolbarContent = styled(Box)({
  flexGrow: 1,
  overflow: "auto",
  backgroundColor: "#FBE5C6",
});

const ToolbarFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  justifyContent: "space-between",
  backgroundColor: "#FBE5C6",
}));

const ToolItem = styled(ListItem)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: "#FBE5C6",
}));

const SubToolGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#f0f0f0",
  width: "100%",
  margin: 0,
}));

const SubToolItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  borderRight: "1px solid #e0e0e0",
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: "#f0f0f0",
}));

// Custom icons to match the design
// const MoveTool = () => (
//   <Box
//     sx={{
//       width: 24,
//       height: 24,
//       display: "flex",
//       alignItems: "center",
//       justifyContent: "center",
//     }}
//   >
//     <svg
//       width="20"
//       height="20"
//       viewBox="0 0 24 24"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path
//         d="M5 9L9 5V8H15V5L19 9L15 13V10H9V13L5 9Z"
//         fill="#FF9A56"
//         stroke="#FF9A56"
//         strokeWidth="1.5"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   </Box>
// );

const DimToggle = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 9L9 5V8H15V5L19 9L15 13V10H9V13L5 9Z"
        fill="black"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const LockRooms = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="2"
        stroke="black"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />
    </svg>
  </Box>
);



const PlotBoundary = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="2"
        stroke="black"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />
    </svg>
  </Box>
);

const WallTransformation = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="7" height="7" fill="black" />
      <rect x="13" y="4" width="7" height="7" fill="black" />
      <rect x="4" y="13" width="7" height="7" fill="black" />
      <rect x="13" y="13" width="7" height="7" fill="black" />
    </svg>
  </Box>
);

const SelectIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 8L10 12L7 16M14 8L11 12L14 16"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const AddIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 5V19M5 12H19"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

// const MoveIcon = () => (
//   <Box
//     sx={{
//       width: 20,
//       height: 20,
//       display: "flex",
//       alignItems: "center",
//       justifyContent: "center",
//     }}
//   >
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path
//         d="M12 5V19M5 12H19M7.5 7.5L16.5 16.5M16.5 7.5L7.5 16.5"
//         stroke="#FF9A56"
//         strokeWidth="1.5"
//         strokeLinecap="round"
//       />
//     </svg>
//   </Box>
// );

const DeleteIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 7L18 7M10 11V17M14 11V17M8 7V5C8 4.44772 8.44772 4 9 4H15C15.5523 4 16 4.44772 16 5V7M18 7V19C18 19.5523 17.5523 20 17 20H7C6.44772 20 6 19.5523 6 19V7H18Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

// const TransformIcon = () => (
//   <Box
//     sx={{
//       width: 20,
//       height: 20,
//       display: "flex",
//       alignItems: "center",
//       justifyContent: "center",
//     }}
//   >
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path
//         d="M15 3L19 7M19 7L15 11M19 7H9M9 21L5 17M5 17L9 13M5 17H15"
//         stroke="#FF9A56"
//         strokeWidth="1.5"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   </Box>
// );

const TextTool = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="2"
        stroke="black"
        strokeWidth="1.5"
      />
      <path
        d="M8 12H16M12 8V16"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

const ComponentsIcon = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 4L20 8V16L12 20L4 16V8L12 4Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 12L20 8M12 12V20M12 12L4 8"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const DoorsIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 4H19C19.5523 4 20 4.44772 20 5V19C20 19.5523 19.5523 20 19 20H5C4.44772 20 4 19.5523 4 19V5C4 4.44772 4.44772 4 5 4Z"
        stroke="black"
        strokeWidth="1.5"
      />
      <path
        d="M15 12C15.5523 12 16 11.5523 16 11C16 10.4477 15.5523 10 15 10C14.4477 10 14 10.4477 14 11C14 11.5523 14.4477 12 15 12Z"
        fill="black"
      />
      <path d="M12 4V20" stroke="black" strokeWidth="1.5" />
    </svg>
  </Box>
);

const WindowIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="4"
        y="6"
        width="16"
        height="12"
        rx="1"
        stroke="black"
        strokeWidth="1.5"
      />
      <path d="M4 12H20M12 6V18" stroke="black" strokeWidth="1.5" />
    </svg>
  </Box>
);

const PenIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.5 5.5L18.5 8.5M13 21H21M3 21L12 12L15 15L3 21Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const Sidebar: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const unit = useSelector(
    (state: RootState) => state.outputEditorButtonState.unit,
  );
  const handleToggleDimensions = useCallback(() => {
    dispatch(toggleUnit());
  }, [dispatch]);

  // Get state from Redux
  const showPlot = useSelector(
    (state: RootState) => state.outputEditorButtonState.showPlot,
  );
  const walls = useSelector(
    (state: RootState) => state.outputEditorButtonState.walls,
  );
  const rooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.rooms,
  );
  const plot_boundary = useSelector(
    (state: RootState) => state.outputEditorButtonState.plot_boundary,
  );
  const undoWalls = useSelector(
    (state: RootState) => state.outputEditorButtonState.undoWalls,
  );
  const undoRooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.undoRooms,
  );
  const redoWalls = useSelector(
    (state: RootState) => state.outputEditorButtonState.redoWalls,
  );
  const redoRooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.redoRooms,
  );
  const doorAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.doorAssets,
  );
  const windowAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.windowAssets,
  );
  const undoDoorAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.undoDoorAssets,
  );
  const undoWindowAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.undoWindowAssets,
  );
  const redoDoorAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.redoDoorAssets,
  );
  const redoWindowAssets = useSelector(
    (state: RootState) => state.outputEditorButtonState.redoWindowAssets,
  );
  const drawLine = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawLine,
  );
  const drawWindow = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawWindow,
  );
  const drawDoor = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawDoor,
  );
  const activeTool = useSelector(
    (state: RootState) => state.outputEditorButtonState.activeTool,
  );
  const selectedWall = useSelector(
    (state: RootState) => state.outputEditorButtonState.selectedWall,
  );
  const selectedWallOption = useSelector(
    (state: RootState) => state.outputEditorButtonState.selectedWallOption,
  );
  const floorPlanWidth = useSelector(
    (state: RootState) => state.outputEditorButtonState.width,
  );
  const floorPlanHeight = useSelector(
    (state: RootState) => state.outputEditorButtonState.height,
  );
  const floorPlans = useSelector((state: RootState) => state.graph.floorPlans);
  const lockRooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.lockRooms,
  );
  const wallTransformationUsed = useSelector(
    (state: RootState) => state.outputEditorButtonState.wallTransformationUsed,
  );
  const index = useSelector((state: RootState) => state.graph.index);
  let graphdata = useSelector((state: any) => state.graph.graphData);
  let plotWidth = graphdata.plot_width;
  let plotHeight = graphdata.plot_height;
  const fromDocuments = useSelector(
    (state: RootState) => state.graph.fromDocuments,
  );
  const plotWidthState = useSelector(
    (state: any) => state.nodesState.plotWidth,
  );
  const plotHeightState = useSelector(
    (state: any) => state.nodesState.plotHeight,
  );
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    "wall-transformation": true,
    components: true,
  });

  // State to track active tool
  // const [activeTool, setActiveTool] = useState<string>("")

  // Update activeTool based on Redux state
  useEffect(() => {
    if (drawLine) {
      setActiveTool("pen");
    } else if (drawWindow) {
      setActiveTool("window");
    } else if (drawDoor) {
      setActiveTool("door");
    }
  }, [drawLine, drawWindow, drawDoor, selectedWall]);

  // Handle wall selection option changes
  useEffect(() => {
    if (selectedWallOption && selectedWall) {
      handleWallOptionChange(selectedWallOption);
    }
  }, [selectedWallOption, selectedWall]);

  // Toggle section expansion
  const handleToggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleToggleCheckbox = () => {
    dispatch(togglePlot());
    dispatch(setCleanUpDone(false));
    setTimeout(() => {

      if (!plot_boundary || plot_boundary.length === 0) {
        drawPlot();
      } else {
        dispatch(setPlot(plot_boundary));
      }
      dispatch(setCleanUpDone(true));
    }, 10);
  };

  const isWallToolActive =
    activeTool === "select" ||
    activeTool === "pen" ||
    activeTool === "delete";

  const handleToggleLockRooms = () => {
    // Don't allow unlocking rooms once a wall transformation tool has been used
    if (wallTransformationUsed && lockRooms) {
      dispatch(
        showToast({
          message:
            "Rooms cannot be unlocked after using wall transformation tools",
          type: "warning",
        }),
      );
      return;
    }
    dispatch(toggleLockRooms());
  };

  const getViewportDimension = (isWidth: boolean): number => {
    if (typeof window === "undefined" || !window.visualViewport) {
      return isWidth ? 1920 : 1080;
    }
    return isWidth ? window.visualViewport.width : window.visualViewport.height;
  };
  const scaleCoordinate = (
    coord: number,
    isX: boolean,
    width: number,
    height: number,
  ) => {
    return coord * ((getViewportDimension(false) * 0.5) / height);
  };

  const drawPlotSave = () => {
    dispatch(setPlot(plot_boundary));
  }

  const drawPlot = () => {
    if (rooms) {
      const currentRooms = rooms || [];

      // Only proceed if there are actual rooms to process
      if (currentRooms.length === 0 || !floorPlanHeight || !floorPlanWidth) return;

      let { minFPX, maxFPX, minFPY, maxFPY } = getFloorplanLimits(currentRooms);

      if (plotWidthState > 0 && plotHeightState > 0) {
        // Use Redux-provided dimensions
        console.log("PLOTWIDTH/HEIGHT---> ", plotWidthState, plotHeightState);

        plotWidth = scaleCoordinate(
          plotWidthState,
          true,
          floorPlanWidth,
          floorPlanHeight,
        );
        plotHeight = scaleCoordinate(
          plotHeightState,
          true,
          floorPlanWidth,
          floorPlanHeight,
        );
        console.log("AFTER -> W and -> H", plotWidth, plotHeight);
        let wall1: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x2: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : maxFPX,
          x1: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : minFPX,
          y1: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
          y2: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
        };
        let wall2: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x1: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : minFPX,
          x2: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : maxFPX,
          y1: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
          y2: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
        };
        let wall3: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x1: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : minFPX,
          x2: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : minFPX,
          y1: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
          y2: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
        };
        let wall4: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x1: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : maxFPX,
          x2: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : maxFPX,
          y1: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
          y2: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
        };
        let new_plot_boundary: Wall[] = [wall1, wall2, wall3, wall4];
        dispatch(setPlot(new_plot_boundary));
      } else {
        let minXWall = Number.MAX_VALUE,
          maxXWall = Number.MIN_VALUE,
          minYWall = Number.MAX_VALUE,
          maxYWall = Number.MIN_VALUE;

        if (Array.isArray(floorPlans) && Array.isArray(floorPlans[index])) {
          floorPlans[index].forEach((apiRoom) => {
            apiRoom?.walls?.forEach((apiWall: any) => {
              minXWall = Math.min(apiWall.x1, apiWall.x2, minXWall);
              minYWall = Math.min(apiWall.y1, apiWall.y2, minYWall);
              maxXWall = Math.max(apiWall.x1, apiWall.x2, maxXWall);
              maxYWall = Math.max(apiWall.y1, apiWall.y2, maxYWall);
            });
          });
        }

        const width = Math.abs(maxXWall - minXWall);
        const height = Math.abs(maxYWall - minYWall);
        console.log("Before -> W and -> H", plotWidth, plotHeight);
        plotWidth = scaleCoordinate(
          graphdata.plot_width,
          true,
          floorPlanWidth,
          floorPlanHeight,
        );
        plotHeight = scaleCoordinate(
          graphdata.plot_height,
          true,
          floorPlanWidth,
          floorPlanHeight,
        );
        console.log("AFTER -> W and -> H", plotWidth, plotHeight);
        let wall1: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x2: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : maxFPX,
          x1: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : minFPX,
          y1: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
          y2: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
        };
        let wall2: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x1: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : minFPX,
          x2: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : maxFPX,
          y1: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
          y2: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
        };
        let wall3: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x1: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : minFPX,
          x2: plotWidth ? (maxFPX + minFPX - plotWidth) / 2 : minFPX,
          y1: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
          y2: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
        };
        let wall4: Wall = {
          id: `plot-${uuidv4()}`,
          roomIds: [],
          x1: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : maxFPX,
          x2: plotWidth ? (maxFPX + minFPX + plotWidth) / 2 : maxFPX,
          y1: plotHeight ? (maxFPY + minFPY - plotHeight) / 2 : minFPY,
          y2: plotHeight ? (maxFPY + minFPY + plotHeight) / 2 : maxFPY,
        };
        let new_plot_boundary: Wall[] = [wall1, wall2, wall3, wall4];
        dispatch(setPlot(new_plot_boundary));
      }
    }
  };
  useEffect(() => {
    if (!showPlot) return;
    if (plot_boundary && plot_boundary.length > 0) {
      drawPlotSave();
    } else {
      drawPlot();
      // Ensure the hook knows to redraw after setting plot boundaries
      dispatch(setCleanUpDone(false));
      setTimeout(() => dispatch(setCleanUpDone(true)), 10);
    }
  }, [showPlot, rooms, floorPlanWidth, floorPlanHeight, dispatch]);

  // Undo function from bottom-nav
  const undo = () => {
    const hasUndoState =
      (undoWalls?.length || 0) > 0 ||
      (undoRooms?.length || 0) > 0 ||
      (undoDoorAssets?.length || 0) > 0 ||
      (undoWindowAssets?.length || 0) > 0;

    if (hasUndoState) {
      dispatch(
        pushRedoStacks({
          rooms: rooms || [],
          walls: walls || [],
          plot: plot_boundary || [],
          doorAssets: doorAssets || [],
          windowAssets: windowAssets || [],
        }),
      );

      if (undoWalls?.length > 0) {
        dispatch(setWalls(undoWalls[undoWalls.length - 1]));
      }

      if (undoRooms?.length > 0) {
        dispatch(setRooms(undoRooms[undoRooms.length - 1]));
      }
      if (undoDoorAssets?.length > 0) {
        dispatch(setDoorAssets(undoDoorAssets[undoDoorAssets.length - 1]));
      }
      if (undoWindowAssets?.length > 0) {
        dispatch(setWindowAssets(undoWindowAssets[undoWindowAssets.length - 1]));
      }

      dispatch(popUndoStacks());
      dispatch(setCleanUpDone(true));
    }
  };

  // Redo function from bottom-nav
  const redo = () => {
    const hasRedoState =
      (redoWalls?.length || 0) > 0 ||
      (redoRooms?.length || 0) > 0 ||
      (redoDoorAssets?.length || 0) > 0 ||
      (redoWindowAssets?.length || 0) > 0;

    if (hasRedoState) {
      dispatch(
        pushUndoStacks({
          rooms: rooms || [],
          walls: walls || [],
          plot: plot_boundary || [],
          doorAssets: doorAssets || [],
          windowAssets: windowAssets || [],
        }),
      );

      if (redoWalls?.length > 0) {
        dispatch(setWalls(redoWalls[redoWalls.length - 1]));
      }

      if (redoRooms?.length > 0) {
        dispatch(setRooms(redoRooms[redoRooms.length - 1]));
      }
      if (redoDoorAssets?.length > 0) {
        dispatch(setDoorAssets(redoDoorAssets[redoDoorAssets.length - 1]));
      }
      if (redoWindowAssets?.length > 0) {
        dispatch(setWindowAssets(redoWindowAssets[redoWindowAssets.length - 1]));
      }

      dispatch(popRedoStacks());
      dispatch(setCleanUpDone(true));
    }
  };

  const handleWallOptionChange = (option: string) => {
    switch (option) {
      case "select":
        console.log("Wall selected:", selectedWall);
        break;
      // case "unselect":
      //   dispatch(setSelectedWall(undefined));
      //   dispatch(setSelectedWallOption("Unselect"));
      //   break;
      case "delete":
        if (selectedWall && walls) {
          dispatch(
            pushUndoStacks({
              rooms: rooms || [],
              walls: walls || [],
              plot: plot_boundary || [],
            }),
          );

          // Filter out the selected wall
          const updatedWalls = walls.filter(
            (wall) =>
              !(
                wall.x1 === selectedWall.x1 &&
                wall.y1 === selectedWall.y1 &&
                wall.x2 === selectedWall.x2 &&
                wall.y2 === selectedWall.y2
              ),
          );

          dispatch(setWalls(updatedWalls));
          dispatch(clearRedoStacks()); // Clear redo stacks after a new action
          dispatch(setSelectedWall(undefined));
          //dispatch(setSelectedWallOption("delete"));
          dispatch(setCleanUpDone(true));
        }
        break;
      default:
        break;
    }
  };

  const handleToolSelect = (toolId: string) => {
    if (activeTool === toolId) {
      setActiveTool("");
      dispatch(setActiveTool(null));
      dispatch(setSelectedWall(undefined));
      dispatch(setSelectedWallOption(null));
      dispatch(setAssetType(null));
      dispatch(setCleanUpDone(false));
      setTimeout(() => dispatch(setCleanUpDone(true)), 10);
    } else {
      setActiveTool(toolId);
      dispatch(setActiveTool(toolId));

      switch (toolId) {
        case "arrow":
          dispatch(clearDrawLine());
          dispatch(clearDrawDoor());
          dispatch(clearDrawWindow());
          dispatch(setAssetType(null));
          dispatch(setSelectedWall(undefined));
          dispatch(setSelectedWallOption("select"));
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        case "select":
          dispatch(clearDrawLine());
          dispatch(clearDrawDoor());
          dispatch(clearDrawWindow());
          dispatch(setAssetType(null));
          dispatch(setSelectedWall(undefined));
          dispatch(setSelectedWallOption("select"));
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        case "plot-boundary":
          dispatch(clearDrawLine());
          dispatch(clearDrawDoor());
          dispatch(clearDrawWindow());
          dispatch(setAssetType(null));
          dispatch(setSelectedWallOption(null));
          if (!showPlot) drawPlot();
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        case "pen":
          dispatch(setDrawLine());
          dispatch(clearDrawDoor());
          dispatch(clearDrawWindow());
          dispatch(setAssetType(null));
          dispatch(setSelectedWallOption(null));
          dispatch(setLockRooms(true));
          dispatch(setWallTransformationUsed());
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        case "delete":
          dispatch(clearDrawLine());
          dispatch(setSelectedWall(undefined));
          dispatch(setSelectedWallOption("delete"));
          dispatch(clearDrawDoor());
          dispatch(clearDrawWindow());
          dispatch(setAssetType(null));
          dispatch(setLockRooms(true));
          dispatch(setWallTransformationUsed());
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        case "window":
          dispatch(clearDrawLine());
          dispatch(clearDrawDoor());
          dispatch(setDrawWindow());
          dispatch(setAssetType("window"));
          dispatch(setSelectedWallOption(null));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        case "door":
          dispatch(clearDrawLine());
          dispatch(clearDrawWindow());
          dispatch(setDrawDoor());
          dispatch(setAssetType("door"));
          dispatch(setSelectedWallOption(null));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          break;
        // case "unselect":
        //   dispatch(clearDrawLine());
        //   dispatch(clearDrawDoor());
        //   dispatch(clearDrawWindow());
        //   dispatch(setAssetType(null));
        //   dispatch(setSelectedWall(undefined));
        //   dispatch(setSelectedWallOption("unselect"));
        //   dispatch(setCleanUpDone(false));
        //   setTimeout(() => dispatch(setCleanUpDone(true)), 10);
        //   break;
        default:
          break;
      }
    }
  };

  return (
    <>
      <ToolbarContainer id="output-sidebar">
        <ToolbarHeader>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ color: "black" }}
          >
            Tool Bar
          </Typography>
        </ToolbarHeader>

        <ToolbarContent>
          <List disablePadding>
            {/* Move Tool */}
            {/* <ToolItem
              onClick={() => handleToolSelect("arrow")}
              sx={{
                cursor: "pointer",
                backgroundColor: activeTool === "arrow" ? "#f0f7ff" : "#FBE5C6",
              }}
            >
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <MoveTool />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Move Tool"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              />
            </ToolItem> */}
            {/* Plot Boundary */}
            <ToolItem id="output-tool-lock-rooms" onClick={() => handleToolSelect("lock-rooms")}>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <LockRooms />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Lock Rooms"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              />
              <Checkbox
                edge="end"
                checked={lockRooms}
                onChange={handleToggleLockRooms}
                disabled={wallTransformationUsed && lockRooms}
                sx={{
                  color: "black",
                  "&.Mui-checked": {
                    color: "black",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
              />
            </ToolItem>
            <Divider />

            <Divider />
            {/* Plot Boundary */}
            <ToolItem id="output-tool-plot-boundary" onClick={() => handleToolSelect("plot-boundary")}>
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <PlotBoundary />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Plot Boundary"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              />
              <Checkbox
                edge="end"
                checked={showPlot}
                onChange={handleToggleCheckbox}
                sx={{
                  color: "black",
                  "&.Mui-checked": {
                    color: "black",
                  },
                }}
              />
            </ToolItem>
            <Divider />

            {/* Wall Transformation */}
            <ToolItem id="output-tool-wall-transformation">
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <WallTransformation />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Wall Transformation"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              />
              <IconButton
                edge="end"
                size="small"
                onClick={() => handleToggleSection("wall-transformation")}
              >
                {expandedSections["wall-transformation"] ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            </ToolItem>

            {/* Wall Transformation Submenu */}
            <Collapse
              in={expandedSections["wall-transformation"]}
              timeout="auto"
              unmountOnExit
            >
              <SubToolGrid container>
                <Grid item xs={6}>
                  <SubToolItem
                    onClick={() => handleToolSelect("select")}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        activeTool === "select" ? "#e3f2fd" : "#f0f0f0",
                    }}
                  >
                    <img src={SelectTool} alt="" />
                    <Typography sx={{ ml: 1, fontSize: "14px" }}>
                      Select
                    </Typography>
                  </SubToolItem>
                </Grid>
                <Grid item xs={6}>
                  <SubToolItem
                    onClick={() => handleToolSelect("pen")}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        activeTool === "pen" ? "#e3f2fd" : "#f0f0f0",
                    }}
                  >
                    <PenIcon />
                    <Typography sx={{ ml: 1, fontSize: "14px" }}>Add</Typography>
                  </SubToolItem>
                </Grid>
                <Grid item xs={6}>
                  <SubToolItem
                    onClick={() => handleToolSelect("delete")}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        activeTool === "delete" ? "#e3f2fd" : "#f0f0f0",
                    }}
                  >
                    <DeleteIcon />
                    <Typography sx={{ ml: 1, fontSize: "14px" }}>
                      Delete
                    </Typography>
                  </SubToolItem>
                </Grid>
              </SubToolGrid>
            </Collapse>

            <Divider />

            {/* Components */}
            <ToolItem id="output-tool-components">
              <ListItemIcon>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f8f8f8",
                  }}
                >
                  <ComponentsIcon />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary="Components"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              />
              <IconButton
                edge="end"
                size="small"
                onClick={() => handleToggleSection("components")}
              >
                {expandedSections["components"] ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            </ToolItem>

            {/* Components Submenu */}
            <Collapse
              in={expandedSections["components"]}
              timeout="auto"
              unmountOnExit
            >
              <SubToolGrid container>
                <Grid item xs={6}>
                  <SubToolItem
                    onClick={() => handleToolSelect("door")}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        activeTool === "door" ? "#e3f2fd" : "#f0f0f0",
                    }}
                  >
                    <DoorsIcon />
                    <Typography sx={{ ml: 1, fontSize: "14px" }}>
                      Doors
                    </Typography>
                  </SubToolItem>
                </Grid>
                <Grid item xs={6}>
                  <SubToolItem
                    onClick={() => handleToolSelect("window")}
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        activeTool === "window" ? "#e3f2fd" : "#f0f0f0",
                    }}
                  >
                    <WindowIcon />
                    <Typography sx={{ ml: 1, fontSize: "14px" }}>
                      Window
                    </Typography>
                  </SubToolItem>
                </Grid>
                {/* <Grid item xs={6}>
                  <Tooltip title="">
                    <SubToolItem
                      sx={{
                        cursor: "not-allowed",
                        opacity: 0.5,
                      }}
                    >
                      <img src={SelectTool} alt="" />
                      <Typography sx={{ ml: 1, fontSize: "14px" }}>Select</Typography>
                    </SubToolItem>
                  </Tooltip>
                </Grid> */}
              </SubToolGrid>
            </Collapse>
            <Divider />
          </List>
        </ToolbarContent>

        <ToolbarFooter sx={{ flexDirection: "column" }}>
            {/* Units Toggle inside Toolbar */}
            <Box
              id="output-tool-units"
              data-tour="output-tool-units"
              sx={{ display: "flex", justifyContent: "center", py: 1.5, borderBottom: "1px solid #e0e0e0" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    color: unit === "feet" ? "#FF9A56" : "#333333",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Feet
                </Typography>
                <Switch
                  checked={unit === "meters"}
                  onChange={handleToggleDimensions}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF9A56",
                      "&:hover": {
                        backgroundColor: "rgba(255, 154, 86, 0.08)",
                      },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF9A56",
                    },
                  }}
                />
                <Typography
                  sx={{
                    color: unit === "meters" ? "#FF9A56" : "#333333",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Meter
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", width: "100%" }}>
              <IconButton
                sx={{ flex: 1, borderRadius: 0, height: 50 }}
                onClick={undo}
                disabled={
                  (undoWalls?.length || 0) === 0 &&
                  (undoRooms?.length || 0) === 0 &&
                  (undoDoorAssets?.length || 0) === 0 &&
                  (undoWindowAssets?.length || 0) === 0
                }
              >
                <Undo />
              </IconButton>
              <Divider orientation="vertical" flexItem />
              <IconButton
                sx={{ flex: 1, borderRadius: 0, height: 50 }}
                onClick={redo}
                disabled={
                  (redoWalls?.length || 0) === 0 &&
                  (redoRooms?.length || 0) === 0 &&
                  (redoDoorAssets?.length || 0) === 0 &&
                  (redoWindowAssets?.length || 0) === 0
                }
              >
                <Redo />
              </IconButton>
            </Box>
        </ToolbarFooter>
      </ToolbarContainer>


      {/* <SelectBar /> */}
    </>
  );
};

export default Sidebar;
